import React, { useState, useEffect } from 'react';
import { navigate } from 'gatsby';
import { Form } from 'antd';
import TextField from 'components/Core/TextField';
import Select from 'components/Core/Select';
import FormLabelWithField from 'components/Core/FormLabelWithField';
import Button from 'components/Core/Button';
import useIndustries from 'hooks/useIndustries';
import { saveForm, init } from 'services/marketoService';
import { convertToolResultToParams } from 'utils/urlHelper';
import { calculateROI } from '../calculations';
import { ROIFormContainer } from '../styles';
import { FormContainer } from '../../styles';
import { Revenue, CallPercentage, SoldPercentage } from '../utils/content';
import EmbedForm from '../../EmbeddableForm';
import EmbedLink from '../../EmbedLink';

const ROIForm = ({ width, height, marketoFormId = 1322, ...otherProps }) => {
  const [formValues, setFormValues] = useState({
    company: '',
    techs: '',
    officeStaff: '',
    revenue: 0,
    calls: '',
    estimates: 0,
    leads: '',
    jobs: 0,
    industry: '',
    otherIndustry: '',
    existingCustomer: '',
    email: '',
  });

  const { industryList, otherIndustryList } = useIndustries();

  const convertParams = values => {
    return convertToolResultToParams(
      Object.keys(values)
        .slice(0, -1)
        .map(x => values[x])
    );
  };

  useEffect(() => {
    init(marketoFormId);
  }, []);

  const submit = async () => {
    const resultUrl = `${otherProps?.pathUrl?.origin}/tools/roi-calculator-results?${convertParams(
      formValues
    )}`;
    const formValuesWithLink = { ...formValues, latestResultsLink: resultUrl };
    setFormValues(formValuesWithLink);
    const results = calculateROI(formValues);

    dataLayer.push({
      event: 'calculator',
      calculator_type: 'ROI Calculator - entries',
      company: formValues.company,
      no_of_technicians: formValues.techs,
      average_ticket_size: formValues.revenue,
      average_calls_per_day: formValues.calls,
      booked_calls_percentage: `${formValues.leads}%`,
      leads_to_estimates_percentage: `${formValues.estimates}%`,
      sold_jobs_percentage: `${formValues.jobs}%`,
      no_of_office_staff: formValues.officeStaff,
      email: formValues.email,
      industry: formValues.industry,
      other_industry: formValues.otherIndustry,
      existing_ST_user: formValues.existingCustomer,
      roiCalcResultLink: resultUrl, // latestResultsLink is not the roght variable. Need to guess what it is.
    });

    await saveForm({
      ...formValues,
      roiCalcWithST: results[0].value,
      roiCalcMonthlyRevenue: results[1].value,
      roiCalcAnnualRevenue: results[2].value,
      roiCalcResultLink: resultUrl,
      marketoFormId,
    });

    navigate(`/tools/roi-calculator-results?${convertParams(formValues)}`);
  };

  const onChange = (id, value) => {
    const tempValues = { ...formValues };
    tempValues[id] = value;
    setFormValues(tempValues);
  };

  return (
    <FormContainer isEmbed={otherProps.isEmbed} {...otherProps}>
      <div className="section-tool-page-form--wrapper">
        <div className="section-tool-page-form--container">
          <ROIFormContainer width={width} height={height} {...otherProps}>
            <p className="roi-form--description">* Required information</p>
            <Form className="roi-form--form" onFinish={submit} data-position="roi form">
              <Form.Item className="roi-form--form-item">
                <FormLabelWithField
                  label="Company name"
                  isRequired
                  helperText="Please input company name"
                >
                  <TextField id="company" handleChange={onChange} required showOnlyFormError />
                </FormLabelWithField>
              </Form.Item>
              <Form.Item className="roi-form--form-item">
                <FormLabelWithField
                  label="What industry do you do the most business in?"
                  isRequired
                  helperText="Indicate what industry you do the majority of your business in. (eg HVAC, Plumbing, Electrical, etc)."
                >
                  <Select
                    id="industry"
                    required
                    placeholder="Select Industry"
                    options={industryList}
                    handleChange={onChange}
                  />
                </FormLabelWithField>
                {formValues['industry'] === 'Other' && (
                  <FormLabelWithField>
                    <Select
                      id="otherIndustry"
                      placeholder="Select from other Industries"
                      options={otherIndustryList}
                      handleChange={onChange}
                    />
                  </FormLabelWithField>
                )}
              </Form.Item>
              <Form.Item className="roi-form--form-item">
                <FormLabelWithField
                  label="Number of techs"
                  isRequired
                  helperText="How many full-time technicians (e.g. plumbers, or electricians) work at your company? For best results, omit apprentices and helpers."
                >
                  <TextField id="techs" handleChange={onChange} required showOnlyFormError />
                </FormLabelWithField>
              </Form.Item>
              <Form.Item className="roi-form--form-item">
                <FormLabelWithField
                  label="Number of office staff"
                  isRequired
                  helperText="How many full-time office employees (eg. manager, dispatchers, CSRs, etc) work at your company?"
                >
                  <TextField
                    type="number"
                    id="officeStaff"
                    handleChange={onChange}
                    required
                    showOnlyFormError
                  />
                </FormLabelWithField>
              </Form.Item>
              <Form.Item className="roi-form--form-item">
                <FormLabelWithField
                  label="Average ticket"
                  isRequired
                  helperText="Please select average ticket"
                >
                  <Select
                    id="revenue"
                    placeholder="Select amount ($)"
                    handleChange={onChange}
                    required
                    options={Revenue}
                  />
                </FormLabelWithField>
              </Form.Item>
              <Form.Item className="roi-form--form-item">
                <FormLabelWithField
                  label="Average number of calls per day"
                  isRequired
                  helperText="Please input average number of calls per day"
                >
                  <TextField
                    type="number"
                    id="calls"
                    handleChange={onChange}
                    required
                    showOnlyFormError
                  />
                </FormLabelWithField>
              </Form.Item>
              <Form.Item className="roi-form--form-item">
                <FormLabelWithField
                  label="What percentage of calls are booked?"
                  isRequired
                  helperText="Please select percentage of calls are booked"
                >
                  <Select
                    id="leads"
                    placeholder="Select percentage (%)"
                    handleChange={onChange}
                    required
                    options={CallPercentage}
                  />
                </FormLabelWithField>
              </Form.Item>
              <Form.Item className="roi-form--form-item">
                <FormLabelWithField
                  label="What percentage of those leads turn into estimates?"
                  isRequired
                  helperText="Please input percentage of those leads turn into estimates"
                >
                  <Select
                    id="estimates"
                    placeholder="Select percentage (%)"
                    handleChange={onChange}
                    required
                    options={CallPercentage}
                  />
                </FormLabelWithField>
              </Form.Item>
              <Form.Item className="roi-form--form-item">
                <FormLabelWithField
                  label="What percentage of those estimates turn into sold jobs?"
                  isRequired
                  helperText="Please select percentage of those estimates turn into sold jobs"
                >
                  <Select
                    id="jobs"
                    dropdownClassName="jobs_select"
                    placeholder="Select percentage (%)"
                    handleChange={onChange}
                    required
                    options={SoldPercentage}
                  />
                </FormLabelWithField>
              </Form.Item>
              <Form.Item className="roi-form--form-item">
                <FormLabelWithField
                  label="Enter Business Email Address"
                  isRequired
                  helperText="Please provide your business email"
                >
                  <TextField
                    id="email"
                    pattern="^([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]{2,5})$"
                    handleChange={onChange}
                    required
                    showOnlyFormError
                  />
                </FormLabelWithField>
              </Form.Item>
              <Form.Item className="roi-form--form-item">
                <FormLabelWithField
                  label="Are you an existing ServiceTitan Customer?"
                  isRequired
                  helperText="Please let us know if you or your company is currently using ServiceTitan."
                >
                  <Select
                    id="existingCustomer"
                    placeholder="Yes or No"
                    handleChange={onChange}
                    required
                    options={['Yes', 'No'].map(x => ({ label: x, value: x }))}
                  />
                </FormLabelWithField>
              </Form.Item>
              <Form.Item data-position="ROI Calculator - Submit CTA">
                <Button
                  htmlType="submit"
                  className="roi-form--submit-button"
                  type="primary"
                  shadow={false}
                  button
                  width="100%"
                >
                  Calculate ROI
                </Button>
              </Form.Item>

              <EmbedLink
                show={!otherProps.isEmbed}
                openCopyEmbedLinkDialog={otherProps?.openCopyEmbedLinkDialog}
              />
            </Form>
          </ROIFormContainer>
        </div>
      </div>
    </FormContainer>
  );
};

export const ROICalcForm = EmbedForm(ROIForm);
